import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { FaFacebookF, FaInstagram, FaYoutube, FaLinkedinIn } from "react-icons/fa"

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, delay },
})

const linkCls =
  'block text-slate-400 transition-colors duration-200 hover:text-[#F97316] focus:outline-none focus-visible:text-[#F97316] focus-visible:underline'

const Heading = ({ children }) => (
  <h3 className="mb-5 text-sm font-bold tracking-wider text-white">{children}</h3>
)

const socials = [
  { label: "Facebook", href: "https://www.facebook.com/profile.php?id=61580041932464", Icon: FaFacebookF, size: 16 },
  { label: "Instagram", href: "https://www.instagram.com/_vectorvisionary", Icon: FaInstagram, size: 18 },
  { label: "YouTube", href: "https://www.youtube.com/@CraftedByVisionary", Icon: FaYoutube, size: 18 },
  { label: "LinkedIn", href: "https://www.linkedin.com/in/max-miller-86952a3a0/", Icon: FaLinkedinIn, size: 16 },
]

const payments = [
  { src: "/payments/visa.png", alt: "VISA" },
  { src: "/payments/mastercard.png", alt: "MasterCard" },
  { src: "/payments/paypal.png", alt: "PayPal" },
  { src: "/payments/creditcard.png", alt: "Credit Card" },
  { src: "/payments/binance.jpeg", alt: "Binance" },
]

const Footer = () => {
  return (
    <footer className="relative overflow-hidden bg-slate-900 text-slate-300">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -left-32 h-80 w-80 rounded-full bg-orange-500/15 blur-3xl"
      />
      {/* stitched top edge */}
      <div aria-hidden="true" className="border-t-2 border-dashed border-[#F97316]/50" />

      <div className="relative mx-auto max-w-7xl px-6 pt-16 pb-10">
        {/* Logo + social row */}
        <motion.div
          {...reveal()}
          className="mb-14 flex flex-col items-center justify-between gap-8 border-b border-white/10 pb-10 md:flex-row"
        >
          <Link to="/" className="flex items-center gap-4 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F97316]">
            <img src="/logo.jpg" alt="Vector Visionary Logo" className="h-16 w-16 rounded-xl" />
            <h2 className="text-3xl font-extrabold tracking-tight text-white">
              <span>Vector</span>
              <span className="text-[#F97316]">Visionary</span>
            </h2>
          </Link>

          <div className="flex flex-wrap justify-center gap-3">
            {socials.map(({ label, href, Icon, size }) => (
              <motion.a
                key={label}
                href={href}
                aria-label={label}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.92 }}
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-slate-300 transition-colors duration-200 hover:border-[#F97316] hover:bg-[#F97316] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F97316]"
              >
                <Icon size={size} />
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* Link columns */}
        <div className="mb-14 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {/* Customer Services */}
          <motion.div {...reveal()}>
            <Heading>CUSTOMER SERVICES</Heading>
            <div className="space-y-3">
              <p className="text-slate-400">(209) 964-6220</p>
              <Link to="/contact" className={linkCls}>Contact Us</Link>
            </div>
          </motion.div>

          {/* Business Hours */}
          <motion.div {...reveal(0.1)}>
            <Heading>BUSINESS HOURS (UTC/GMT-7)</Heading>
            <div className="space-y-3">
              <p className="text-slate-400">Monday-Friday : 9am - 5pm</p>
              <p className="text-slate-400">Saturday-Sunday : (Closed)</p>
            </div>
          </motion.div>

          {/* Company */}
          <motion.div {...reveal(0.2)}>
            <Heading>COMPANY</Heading>
            <div className="space-y-3">
              <Link to="/company" className={linkCls}>About Us</Link>
              <Link to="/portfolio" className={linkCls}>Portfolio</Link>
              <Link to="/company" className={linkCls}>Why Choose Us</Link>
              <Link to="/services" className={linkCls}>Services</Link>
            </div>
          </motion.div>

          {/* Policies */}
          <motion.div {...reveal(0.3)}>
            <Heading>POLICIES</Heading>
            <div className="space-y-3">
              <Link to="/privacy" className={linkCls}>Privacy policy</Link>
              <Link to="/terms" className={linkCls}>Terms of Use</Link>
              <Link to="/acknowledgement" className={linkCls}>Refund Policy</Link>
            </div>
          </motion.div>
        </div>

        {/* Payment Methods */}
        <motion.div
          {...reveal()}
          className="mb-10 rounded-3xl border border-dashed border-white/15 bg-white/[0.03] p-6 md:flex md:items-center md:gap-8"
        >
          <h3 className="mb-4 text-sm font-bold tracking-wider text-white md:mb-0 md:shrink-0">We Accept</h3>
          <div className="flex flex-wrap gap-3">
            {payments.map((p) => (
              <motion.img
                key={p.alt}
                src={p.src}
                alt={p.alt}
                className="h-12 w-16 rounded-lg bg-white object-contain p-1 shadow-md"
                whileHover={{ scale: 1.06 }}
                transition={{ duration: 0.25 }}
              />
            ))}
          </div>
        </motion.div>

        {/* Copyright */}
        <motion.div {...reveal()} className="border-t border-white/10 pt-8 text-center">
          <p className="text-sm text-slate-500">© 2024 Vector Visionary. All Rights Reserved.</p>
        </motion.div>
      </div>
    </footer>
  )
}

export default Footer